import { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack, Theme, useMediaQuery } from "@mui/material";
import { RouterLink } from "components/router-link";
import MenuIcon from "@mui/icons-material/Menu";
import TopNavSection from "./TopNavSection";
import { usePathname } from "hooks/use-pathname";
import { useTheme } from "@mui/material/styles";
import { useAuthRouteRedirection, useMakeUserAllowedSections } from "layouts/Config";
import AccountPopover from "./AccountPopover";
import WorkspaceSwitcher from "layouts/components/WorkspaceSwitcher";
import side_nav_inkfree_logo from "assets/images/side_nav_inkfree_logo.svg";

import { useAppSelector } from "redux/hooks";
import { selectTheme } from "redux/preferencesSlice";

const useCssVars = (): Record<string, string> => {
  const themePreference = useAppSelector(selectTheme);
  const theme = useTheme();

  return useMemo((): Record<string, string> => {
    if (themePreference === "dark") {
      return {
        "--nav-bg": theme.palette.neutral[50],
        "--nav-color": theme.palette.neutral[100],
        "--nav-divider-color": theme.palette.neutral[800],
        "--nav-border-color": theme.palette.neutral[50],
        "--nav-logo-border": theme.palette.neutral[700],
        "--nav-item-color": theme.palette.neutral[500],
        "--nav-item-hover-bg": theme.palette.primary.main,
        "--nav-item-active-bg": theme.palette.primary.main,
        "--nav-item-disabled-color": theme.palette.neutral[600],
        "--nav-item-icon-color": theme.palette.neutral[500],
        "--nav-item-icon-active-color": theme.palette.common.white,
        "--nav-item-icon-disabled-color": theme.palette.neutral[700],
        "--nav-item-chevron-color": theme.palette.neutral[700],
        "--nav-scrollbar-color": theme.palette.neutral[400],
        "--nav-item-active-color": theme.palette.common.white,
      };
    } else {
      return {
        "--nav-bg": theme.palette.neutral[900],
        "--nav-color": theme.palette.common.white,
        "--nav-divider-color": theme.palette.neutral[800],
        "--nav-border-color": theme.palette.neutral[100],
        "--nav-logo-border": theme.palette.neutral[900],
        "--nav-section-title-color": theme.palette.neutral[400],
        "--nav-item-color": theme.palette.neutral[500],
        "--nav-item-hover-bg": theme.palette.neutral[600],
        "--nav-item-active-bg": theme.palette.neutral[900],
        "--nav-item-active-color": theme.palette.common.white,
        "--nav-item-disabled-color": theme.palette.neutral[400],
        "--nav-item-icon-color": theme.palette.neutral[500],
        "--nav-item-icon-active-color": theme.palette.common.white,
        "--nav-item-icon-disabled-color": theme.palette.neutral[400],
        "--nav-item-chevron-color": theme.palette.neutral[400],
        "--nav-scrollbar-color": theme.palette.neutral[900],
      };
    }
  }, [theme]);
};

interface CustomProps {
  onMobileNav: () => void;
}

const TopNav = (props: CustomProps) => {
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const cssVars = useCssVars();
  const sections = useMakeUserAllowedSections();
  const authRoute = useAuthRouteRedirection();
  const pathname = usePathname();
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const themePreference = useAppSelector(selectTheme);
  const isDarkTheme = themePreference === "dark";

  useEffect(() => {
    const container = document.getElementById("nav-items");
    if (container && container.scrollWidth > container.clientWidth) {
      setHasScrollbar(true);
    } else {
      setHasScrollbar(false);
    }
  }, [mdUp]);

  return (
    <Box
      component="header"
      sx={{
        ...cssVars,
        backgroundColor: mdUp ? "var(--nav-bg)" : "transparent",
        borderBottomColor: "var(--nav-border-color)",
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
        color: "var(--nav-color)",
        left: 0,
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{
          px: 3,
          py: 1,
        }}
      >
        <Stack alignItems="center" direction="row" spacing={2}>
          {!mdUp && (
            <IconButton
              aria-label="Open navigation menu"
              onClick={props.onMobileNav}
            >
              <MenuIcon />
            </IconButton>
          )}
          {mdUp && (
            <Box
              component={RouterLink}
              href={authRoute}
              aria-label="InkFree home"
              sx={{
                display: "inline-flex",
                height: 40,
                p: "4px",
                width: 40,
              }}
            >
              <img
                src={side_nav_inkfree_logo}
                width="200px"
                alt=""
                aria-hidden="true"
              />
            </Box>
          )}
        </Stack>
        <Stack alignItems="center" direction="row" spacing={2}>
          <WorkspaceSwitcher />
          <AccountPopover />
        </Stack>
      </Stack>
      {mdUp && (
        <Box
          sx={{
            borderTopWidth: 1,
            borderTopStyle: "solid",
            borderTopColor: "var(--nav-divider-color)",
          }}
        >
          <Stack
            id="nav-items"
            alignItems="center"
            component="nav"
            aria-label="Main"
            direction="row"
            spacing={1}
            sx={(theme) => ({
              px: 2,
              pt: 1.5,
              pb: hasScrollbar ? 0 : 1.5,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "15px",
              },
              "&::-webkit-scrollbar-thumb": {
                border: "5px solid rgba(0, 0, 0, 0)",
                backgroundClip: "padding-box",
                borderRadius: " 9999px",
                backgroundColor: theme.palette.neutral[900],
              },
              "&:hover": {
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.neutral[400],
                },
              },
            })}
          >
            {sections.map((section: any, index: any) => (
              <TopNavSection
                items={section.items}
                key={index}
                pathname={pathname}
                subheader={section.subheader}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default TopNav;
